import { Network } from '@aptos-labs/ts-sdk';
import { WalletReadyState } from '@aptos-labs/wallet-adapter-core';
import {
  CenterCol,
  CenterRow,
  Col,
  Image,
  ScrollView,
  Txt,
} from '@aries/mobile-core/components';
import {
  NotificationBase,
  Notify,
  openModal,
} from '@aries/mobile-core/Smoothy';
import { useLatest } from '@aries/shared/hooks';
import { Trans, useTranslation } from '@aries/shared/locale';
import {
  APTOS_MAINNET_API_KEY,
  shortenAddress,
} from '@aries/shared/utils';
import { globalToken } from '@aries/ui-theming';
import { sortBy, uniqBy } from 'lodash';
import React, { ReactNode, useEffect } from 'react';
import { TouchableOpacity } from 'react-native';
import styled from 'styled-components/native';
import { OKXWalletName } from '../adapters/okx-adapter';
import { WALLET_POSITION_MAP } from '../config';
import { useWallet } from '../hook';
import { useWallet as useWalletInner } from './context-impl';
import { AptosWalletAdapterProvider as WalletProviderImpl } from './provider-impl';

export const WalletProvider = ({
  children,
  isTestnet,
  useAptosMainnetApiKey,
}: {
  children: ReactNode;
  isTestnet: boolean;
  useAptosMainnetApiKey?: boolean;
}) => {
  const network = isTestnet ? Network.TESTNET : Network.MAINNET;
  const dappConfigKey = `${network}:${
    useAptosMainnetApiKey ? 'aptos-api' : 'default'
  }`;

  return (
    <WalletProviderImpl
      key={dappConfigKey}
      autoConnect={false}
      dappConfig={{
        network,
        ...(useAptosMainnetApiKey
          ? {
              aptosApiKeys: {
                [Network.MAINNET]: APTOS_MAINNET_API_KEY,
              },
            }
          : {}),
      }}
    >
      <>
        {children}
        <SelectWalletModal />
        <WalletCtxSpy />
      </>
    </WalletProviderImpl>
  );
};

const WalletCtxSpy = () => {
  const { t } = useTranslation();
  const { setWalletCtx } = useWallet();
  const adapter = useWalletInner();
  const { wallet, connected, account } = adapter;
  useEffect(() => {
    if (connected && wallet && account?.address) {
      const address = account.address.toString();

      const walletCtx = {
        adapter: {
          ...adapter,
          disconnect: () => {
            try {
              adapter.disconnect();
              window.location.reload();
              // eslint-disable-next-line no-empty
            } catch (error) {}
          },
        },
        address,
      };

      setWalletCtx(walletCtx);

      Notify.info(
        t`Wallet Connected!`,
        t`Address: ${shortenAddress(address)}`,
      );
    } else {
      setWalletCtx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, wallet, account?.address, Notify]);

  return null;
};

const SelectWalletModal: React.FC<{}> = () => {
  const { walletModalShow, setWalletModalShow } = useWallet();

  const {
    wallet,
    disconnect,
    connect,
    wallets: adapters,
  } = useWalletInner();
  const connectRef = useLatest(connect);
  const disconnectRef = useLatest(disconnect);

  useEffect(() => {
    if (walletModalShow) {
      openModal({
        title: 'Select Wallet',
        renderContent: ({ close }) => {
          return (
            <ScrollView>
              <CenterCol>
                <Txt success mb={4} h3 bold>
                  <Trans i18nkey="Powered by AIP-62 Wallet Standard" />
                </Txt>
              </CenterCol>
              <NotificationBase
                type="info"
                title={<Trans i18nkey="REMIND" />}
                style={{ paddingTop: 8, paddingBottom: 8 }}
                content={
                  <Txt h2 c2>
                    <Trans i18nkey="Please switch your wallet to" />{' '}
                    <Txt h3 warning>
                      <Trans i18nkey="CORRECT NETWORK" />
                    </Txt>
                    .
                  </Txt>
                }
              />
              {sortBy(
                uniqBy(adapters ?? [], a => a.name).filter(
                  _adapter =>
                    !['T wallet', 'OKX Wallet'].includes(_adapter.name),
                ),
                _adapter =>
                  _adapter.readyState === WalletReadyState.Installed
                    ? WALLET_POSITION_MAP[_adapter.name] ?? 1000
                    : (WALLET_POSITION_MAP[_adapter.name] ?? 1000) * 10000,
              ).map(_adapter => {
                const i = { ..._adapter };

                return (
                  <TouchableOpacity
                    key={i.name}
                    onPress={async () => {
                      try {
                        await disconnectRef.current();
                        // eslint-disable-next-line no-empty
                      } catch {}
                      setTimeout(async () => {
                        try {
                          if (
                            i.readyState !== WalletReadyState.Installed &&
                            !i.name.includes('Mizu')
                          ) {
                            window.open(i.url);
                          } else {
                            if (i.name === 'Mizu Wallet') {
                              try {
                                await fetch(
                                  'https://assets.mz.xyz/static/config/mizuwallet-connect-manifest.json',
                                );
                              } catch (error) {
                                Notify.error(
                                  'Mizu is unavailable in your country.',
                                );
                                return;
                              }
                            }

                            await connectRef.current(i.name);
                            close();
                          }
                          // eslint-disable-next-line no-empty
                        } catch {}
                      }, 150);
                    }}
                  >
                    <WalletItem
                      disabled={false}
                      isActive={wallet?.name === i.name}
                      mt={10}
                    >
                      <Image
                        style={{ height: 45, width: 45 }}
                        source={{ uri: i.icon }}
                      />
                      <Col ml={15}>
                        <Txt h3 bold>
                          {_adapter.name === OKXWalletName
                            ? 'OKX Wallet'
                            : _adapter.name}
                        </Txt>
                        <Txt
                          c2={i.readyState !== WalletReadyState.Installed}
                          h2
                        >
                          {i.readyState}
                        </Txt>
                      </Col>
                    </WalletItem>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          );
        },
      });
      setWalletModalShow(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletModalShow]);
  return null;
};

const { colorBgQuaternary, colorBgElevated, colorBgDisabled } =
  globalToken;
// @ts-ignore
const WalletItem = styled(CenterRow)<{
  isActive: boolean;
  disabled: boolean;
}>`
  padding-left: 15px;
  background-color: ${p =>
    p.disabled
      ? colorBgDisabled
      : p.isActive
      ? colorBgQuaternary
      : undefined};
  cursor: pointer;
  border-radius: 5px;
  :hover {
    background-color: ${colorBgElevated};
  }
  padding: 15px 12px;
  width: 440px;
`;
